import { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { Box } from '@chakra-ui/react';

import { KycStatus, QesStatus } from '__generated__/GQL';
import config from 'config';
import asBentoPage from 'common/bento/hoc/asBentoPage';
import webView from 'common/webView';
import { currentAppVersionIsGte } from 'common/webView/helpers/versionIsGte';
import FourthlineSdk from 'features/Onboarding/components/FourthlineSdk/FourthlineSdk';
import OBNavigation from 'features/Onboarding/components/OBNavigation/OBNavigation';
import identityMachine, {
  State as IdentityState,
} from 'features/Onboarding/modules/Identity/machine/machine';
import verificationMachine, {
  State as VerificationState,
} from 'features/Onboarding/modules/Verification/machine/machine';

import { GetKycIdStatusDocument } from '../../graphql/queries/getKycIdStatus.gql';

interface FourthlineIdCheckProps {
  companyProfileId: string;
  fourthlineToken: string;
  onVerificationCompleted: () => void;
  onRestartIdCheck: () => void;
}

const TRACKING_EVENTS = {
  sdkLoaded: 'Onboarding Fourthline SDK Loaded',
};

const FourthlineIdCheck = asBentoPage<FourthlineIdCheckProps>(
  ({
    companyProfileId,
    fourthlineToken,
    Layout,
    onPrev,
    onRestartIdCheck,
    onVerificationCompleted,
  }) => {
    /**
     * @todo(LED-3550) remove on 1.270.0
     *
     * This light, polling refetch is used on mobile devices (either webView or mobile browser).
     * The signing happens on another window and we know the device may suspend the web-app.
     * When we come back, we want to be informed of the flow validation (sync via Fourthline back-end webhook)
     */
    useQuery(GetKycIdStatusDocument, {
      onCompleted({ viewer: { companyUser } }) {
        const { kycIdStatus, qesStatus } = companyUser;

        if (
          [
            KycStatus.Pending,
            KycStatus.Prevalidated,
            KycStatus.Validated,
          ].includes(kycIdStatus) &&
          [QesStatus.AwaitingValidation, QesStatus.Signed].includes(qesStatus)
        ) {
          onVerificationCompleted();
        }
      },
      pollInterval: 1000,
      skip: !webView.isInWebView,
      variables: { companyProfileId },
    });

    const successRedirectUrlBase = useMemo(() => {
      // In the webview, we redirect to a deeplink instead of the web-app url.
      if (
        webView.isInWebView &&
        // But only starting in `1.223.0` !
        // Before that, we stayed in the webView anyway and the deeplink wouldn't work.
        currentAppVersionIsGte('1.223.0')
      ) {
        return 'shine://tabs/home';
      }

      const url = new URL(`${config.shineAppHost}/onboarding`);

      url.searchParams.set(
        `bootstrap:${verificationMachine.id}`,
        VerificationState.IDENTITY,
      );

      url.searchParams.set(
        `bootstrap:${identityMachine.id}`,
        IdentityState.POST_FOURTHLINE_ID_CHECK_REDIRECTION,
      );

      return url.toString();
    }, []);

    const failureRedirectUrlBase = useMemo(() => {
      // In the webview, we redirect to a deeplink instead of the web-app url.
      if (
        webView.isInWebView &&
        // But only starting in `1.223.0` !
        // Before that, we stayed in the webView anyway and the deeplink wouldn't work.
        currentAppVersionIsGte('1.223.0')
      ) {
        return 'shine://tabs/home';
      }

      const url = new URL(`${config.shineAppHost}/onboarding`);

      return url.toString();
    }, []);

    return (
      <Layout
        data-testid="fourthline-identity"
        nav={<OBNavigation onPrev={onPrev} />}
      >
        <Box height="full" paddingBottom="space-64">
          <FourthlineSdk
            failureRedirectUrl={failureRedirectUrlBase}
            onFlowCompleted={onVerificationCompleted}
            onRestartFlow={onRestartIdCheck}
            successRedirectUrl={successRedirectUrlBase}
            token={fourthlineToken}
            trackingEvents={TRACKING_EVENTS}
          />
        </Box>
      </Layout>
    );
  },
);

export default FourthlineIdCheck;
